// The one thing that moves a prospect forward at its current stage.
//
// Every panel already knows which of its own buttons the stage allows, but that
// left the screen with no answer to the question an agent actually opens it
// with: what do I do now? The loudest controls were the stage overrides at the
// top, while the action that advances the customer sat inside a panel further
// down. This resolves the stage to a single next step — its words, its button,
// and where that button lives — so the header can lead with it and the override
// actions can step back.
//
// State gating is not duplicated here: each case mirrors the same helper the
// owning panel uses, so a step is never offered that the API would refuse.

import {
  ConfigEmailDispatch,
  EichrechtReview,
  OnboardingReminders,
  OnboardingState,
  SetupFeeStatus,
  SignMethod,
} from "../types/onboarding";
import {
  canPromoteToCustomer,
  canRegisterStations,
  canSendConfigEmail,
  canSendContract,
  isConfigEmailSent,
  isEsignAwaited,
  nextActionFor,
  publishActionFor,
  recordActionFor,
  sendActionFor,
} from "./onboardingStates";
import { ContractChase } from "./contractChase";
import { isCustomerPromoted, SETUP_FEE_NEXT_ACTION } from "./setupFee";
import { ProspectTabKey } from "./prospectTabs";
import { SETUP_EROUND_LABEL, StationSetupNext } from "./stationSetup";

export type NextStepAction =
  | "log-reminder"
  // CWA-230: support's Eichrecht check of an unlisted wallbox.
  | "check-eichrecht"
  | "assign-station-ids"
  | "generate-draft"
  | "send-contract"
  | "record-signed"
  // CWA-231: confirm the access data, promote, and send the stations to eRound, in one approval.
  | "setup-eround"
  | "review-eround"
  | "setup-fee"
  | "send-config-email"
  | "publish"
  // CWA-189: the reminders escalated ("Needs follow-up"); log the call in the Reminders card.
  | "follow-up-call";

export interface NextStep {
  // What happens next in plain words — said whether or not ops can act on it.
  description: string | null;
  // The single action that moves this prospect on, where ops owns it.
  action: NextStepAction | null;
  label: string | null;
  // The tab holding that action's own controls, so the header can take an agent
  // straight to it. Null for an action that lives in the header itself.
  tab: ProspectTabKey | null;
  // True where the normal course is to wait — the move belongs to the customer,
  // or to a periodic check. An action may still be offered (an agent who can see
  // the station is up need not wait for the report), but it is not urged.
  waiting: boolean;
  // The station a "review-eround" step is about.
  stationId?: string | null;
  // A page elsewhere that fixes what the prospect waits on (an unmapped wallbox model).
  href?: string | null;
  // Shown with whatever the step is: a wallbox marked not eichrechtskonform (CWA-230).
  warning?: string;
}

const step = (
  action: NextStepAction | null,
  label: string | null,
  tab: ProspectTabKey | null,
  waiting = false
): Omit<NextStep, "description"> => ({ action, label, tab, waiting });

// The send action's own label, which depends on how the customer signs — the
// same three the contract panel offers.
const sendStep = (method: SignMethod | null | undefined) => {
  switch (sendActionFor(method)) {
    case "send":
      return step("send-contract", "Send contract", "contract");
    case "esign-sent":
      return step("send-contract", "Mark e-sign as sent", "contract");
    case "hub-sign":
      return step("send-contract", "Send signing link via email", "contract");
    default:
      // No signature method captured: the panel says to set one first rather
      // than guessing, so the header offers nothing either.
      return step(null, null, "contract");
  }
};

// Recording the signature is an ops step for the manual and digital tracks only
// — click-to-sign completes in the Hub, so there it is a wait.
const recordStep = (state: OnboardingState, method: SignMethod | null | undefined) => {
  const action = recordActionFor(method);
  if (action === "upload-signed" && state === "CONTRACT_SENT") {
    return step("record-signed", "Upload signed contract", "contract");
  }
  if (action === "confirm-signed" && isEsignAwaited(state)) {
    return step("record-signed", "Confirm signed", "contract");
  }
  return step(null, null, "contract", true);
};

const resolve = (
  state: OnboardingState,
  method: SignMethod | null | undefined,
  chase?: ContractChase | null,
  setupFee?: SetupFeeStatus | null
): Omit<NextStep, "description"> => {
  // An unsigned contract that has been out for a week outranks whatever the
  // stage would otherwise suggest: recording a signature nobody has given is
  // not the move, chasing the customer is. Nothing here sends anything — the
  // agent makes the call, then logs it (see utils/contractChase).
  if (chase?.due) {
    return step("log-reminder", "Log a reminder", "contract");
  }
  // Station IDs are assigned from the header itself, so no tab to open.
  if (state === "FORM_SUBMITTED") {
    return step("assign-station-ids", "Assign station IDs", null);
  }
  if (state === "STATION_ID_ASSIGNED") {
    return step("generate-draft", "Generate contract draft", null);
  }
  if (canSendContract(state)) return sendStep(method);
  if (state === "CONTRACT_SENT" || isEsignAwaited(state)) {
    return recordStep(state, method);
  }
  // One approval confirms the access data, promotes and sends the stations to
  // eRound (CWA-231); the station setup, where known, says it in more detail.
  if (canRegisterStations(state) || canPromoteToCustomer(state)) {
    return step("setup-eround", SETUP_EROUND_LABEL, "stations");
  }
  // The setup fee (CWA-196) falls due at promotion and is not a stage: it comes
  // first while it is open, for a customer who went live long ago as much as
  // for one just promoted, but it never disables the steps after it (FR-010).
  // A pending invoice is not urged — its retry lives in the setup-fee card.
  if (isCustomerPromoted(state) && setupFee === "OPEN") {
    return step("setup-fee", "Handle setup fee", "stations");
  }
  if (canSendConfigEmail(state) && !isConfigEmailSent(state)) {
    return step("send-config-email", "Send configuration email", "stations");
  }
  // Publishing: urged once the stations have reported themselves available,
  // offered but not urged before that.
  const publish = publishActionFor(state);
  if (publish === "confirm-publication") {
    return step("publish", "Confirm publication", "stations");
  }
  if (publish === "go-live") {
    return step("publish", "Mark as live", "stations", true);
  }
  // Waiting on the customer, on a periodic check, or finished.
  return step(null, null, null, true);
};

// CWA-232: the configuration email goes out on its own once eRound has every
// station. While it is going out there is nothing to press; a failed or stuck
// send is handed back to the agent, ahead of an open setup fee — the customer
// can't configure the station without the email, while the fee is invoicing.
const configEmailStep = (
  state: OnboardingState,
  dispatch: ConfigEmailDispatch | null | undefined
): NextStep | null => {
  if (state !== "CUSTOMER_PROMOTED" || !dispatch) return null;
  const failed = dispatch.status === "FAILED";
  const stuck = dispatch.status === "PENDING" && dispatch.stale;
  if (failed || stuck) {
    const reason = failed
      ? `Configuration email failed${dispatch.failure_message ? `: ${dispatch.failure_message}` : ""}.`
      : "Configuration email got stuck while sending.";
    return {
      description: `${reason} Send it again.`,
      ...step("send-config-email", "Send configuration email", "stations"),
    };
  }
  if (dispatch.status === "PENDING") {
    return { description: "Sending the configuration email…", ...step(null, null, "stations", true) };
  }
  return null;
};

const fromSetup = ({ description, action, label, waiting, stationId, href }: StationSetupNext): NextStep => ({
  description,
  action,
  label,
  tab: href ? null : "stations",
  waiting,
  stationId,
  ...(href ? { href } : {}),
});

// CWA-230: an unlisted wallbox waits for support's check before station IDs.
const eichrechtStep = (review?: EichrechtReview | null): NextStep | null =>
  review?.status === "PENDING"
    ? {
        description: `Check that ${review.manufacturer} ${review.model} is eichrechtskonform.`,
        ...step("check-eichrecht", "Check Eichrecht", null),
      }
    : null;

// Not conform blocks nobody, so it rides along with every step until support changes the decision.
const eichrechtWarning = (review?: EichrechtReview | null): string | null =>
  review?.status === "NOT_CONFORM"
    ? `${review.manufacturer} ${review.model} is marked not eichrechtskonform${review.note ? `: ${review.note}` : "."}`
    : null;

// CWA-189: once the reminder ladder escalates, the prospect needs a person. On
// the customer's move that is a call (then logged); on support's own move it is
// the stage's work, now overdue, so its action stays and only the words change.
const followUpStep = (base: NextStep, reminders?: OnboardingReminders | null): NextStep => {
  const waiting = reminders?.step;
  if (!reminders?.needs_follow_up || !waiting) return base;
  const days = waiting.days_waiting;
  if (waiting.owner === "CUSTOMER") {
    return {
      ...base,
      description: `Call the customer: no response after reminders (${days} ${days === 1 ? "day" : "days"}).`,
      ...step("follow-up-call", "Log call", "progress"),
    };
  }
  const description = `Pick this up: waiting on us for ${days} ${days === 1 ? "day" : "days"}.`;
  if (base.action) return { ...base, description, waiting: false };
  return { ...base, description, ...step("follow-up-call", "Log follow-up", "progress") };
};

export const nextStepFor = (
  state: OnboardingState,
  signMethod?: SignMethod | null,
  chase?: ContractChase | null,
  setupFee?: SetupFeeStatus | null,
  // "Access data & eRound" (utils/stationSetup), while the prospect is in it:
  // the progress tab tells that step from the same answer, so the two agree.
  setup?: StationSetupNext | null,
  configEmail?: ConfigEmailDispatch | null,
  // What eRound still needs once the customer is promoted (CWA-231).
  eround?: StationSetupNext | null,
  eichrecht?: EichrechtReview | null,
  // CWA-189: the reminder ladder from the prospect detail.
  reminders?: OnboardingReminders | null
): NextStep => {
  // While the automatic reminders run, reminder 2 already chases an unsigned
  // contract, so the manual chase is not the step (research R9). The contract
  // tab's own "Log reminder" button stays.
  const manualChase = reminders?.enabled ? null : chase;
  const next = followUpStep(
    eichrechtStep(eichrecht) ?? stepFor(state, signMethod, manualChase, setupFee, setup, configEmail, eround),
    reminders
  );
  const warning = eichrechtWarning(eichrecht);
  return warning ? { ...next, warning } : next;
};

const stepFor = (
  state: OnboardingState,
  signMethod?: SignMethod | null,
  chase?: ContractChase | null,
  setupFee?: SetupFeeStatus | null,
  setup?: StationSetupNext | null,
  configEmail?: ConfigEmailDispatch | null,
  eround?: StationSetupNext | null
): NextStep => {
  if (setup) return fromSetup(setup);
  // A failed or blocked station is a task; the configuration email can't help the customer before it.
  if (eround && !eround.waiting) return fromSetup(eround);
  const emailStep = configEmailStep(state, configEmail);
  if (emailStep) return emailStep;
  // Waiting for eRound says so, unless an open setup fee gives the agent something to do meanwhile.
  if (eround && !(isCustomerPromoted(state) && setupFee === "OPEN")) return fromSetup(eround);
  const resolved = resolve(state, signMethod, chase, setupFee);
  const description = chase?.due
    ? "Chase the customer for the signature, then log the reminder."
    : resolved.action === "setup-fee"
    ? SETUP_FEE_NEXT_ACTION
    : nextActionFor(state);
  return { description, ...resolved };
};
